import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';
import {
  isRecognitionConfident,
  RECOGNITION_CONFIDENCE_EXPONENT,
  RECOGNITION_CONFIDENCE_PROFILE_LABEL,
  RECOGNITION_CONFIDENCE_THRESHOLD,
  RECOGNITION_CONFIDENCE_THRESHOLD_RAW_COSINE,
  RECOGNITION_RUNNER_UP_FLOOR,
} from '../../constants/recognitionReview';

/**
 * Matrix axes use the production display similarity and delta shown elsewhere in Forge; raw cosine
 * normalization stays internal. Columns are the top candidate's similarity, rows are its delta to
 * the best different-item-number candidate.
 */
const SIMILARITY_AXIS = [0.7, 0.75, 0.8, 0.85, 0.9, 0.95] as const;
const DELTA_AXIS = [0.3, 0.25, 0.2, 0.15, 0.1, 0.05] as const;

const CELL_WIDTH = 34;
const CELL_HEIGHT = 21;
const AXIS_GUTTER = 34;
const AXIS_FOOTER = 30;
const MATRIX_WIDTH = AXIS_GUTTER + SIMILARITY_AXIS.length * CELL_WIDTH;
const MATRIX_HEIGHT = DELTA_AXIS.length * CELL_HEIGHT + AXIS_FOOTER;

/** Ties the trigger to the popover for assistive tech; only one popover is ever open at a time. */
const POPOVER_ID = 'rr-confidence-info-popover';

function formatAxisValue(value: number): string {
  return value.toFixed(2).replace(/^0/, '');
}

/**
 * Explains the `Recommended` Recognition confidence profile: plain language first, the
 * similarity-by-delta matrix second, and the formula plus exact threshold as detail last.
 *
 * Shared by the Recognition OKRs dashboard, the Recognition review tool, and Remote DC so all three
 * describe confidence the same way.
 */
export const ConfidenceMatrixInfo: React.FC<{ className?: string }> = ({ className }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsOpen(false);
      }
    };
    const handlePointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen]);

  return (
    <div className={`rr-info${className ? ` ${className}` : ''}`} ref={containerRef}>
      <button
        type="button"
        className="rr-info-button"
        aria-label="How Recognition confidence is decided"
        aria-expanded={isOpen}
        aria-controls={POPOVER_ID}
        onClick={() => setIsOpen((open) => !open)}
      >
        <InfoOutlinedIcon style={{ fontSize: 14 }} />
      </button>
      {isOpen && (
        <div
          id={POPOVER_ID}
          className="rr-info-popover"
          role="dialog"
          aria-label="Recognition confidence profile"
        >
          <div className="rr-info-title">{RECOGNITION_CONFIDENCE_PROFILE_LABEL} confidence</div>
          <p className="rr-info-copy">
            A match counts as confident when the top candidate stands clearly apart from the best
            candidate with a different item number. A high similarity on its own is not enough: a
            close runner-up means the model could not tell the two products apart, so the result is
            unconfident even at 0.95 similarity.
          </p>
          <p className="rr-info-copy">
            The gap that counts as &ldquo;clear&rdquo; shrinks as similarity rises, because the room
            left above the runner-up shrinks too. Shaded cells below are confident.
          </p>
          <svg
            className="rr-info-matrix"
            width={MATRIX_WIDTH}
            height={MATRIX_HEIGHT}
            viewBox={`0 0 ${MATRIX_WIDTH} ${MATRIX_HEIGHT}`}
            role="img"
            aria-label="Similarity by delta matrix shading confident regions"
          >
            {DELTA_AXIS.map((delta, rowIndex) => (
              <g key={delta}>
                <text
                  className="rr-info-matrix-axis"
                  x={AXIS_GUTTER - 5}
                  y={rowIndex * CELL_HEIGHT + CELL_HEIGHT / 2 + 3}
                  textAnchor="end"
                >
                  {formatAxisValue(delta)}
                </text>
                {SIMILARITY_AXIS.map((similarity, columnIndex) => {
                  const confident = isRecognitionConfident(similarity, similarity - delta);
                  return (
                    <rect
                      key={similarity}
                      data-testid={`confidence-cell-${similarity}-${delta}`}
                      className={`rr-info-matrix-cell ${confident ? 'is-confident' : 'is-unconfident'}`}
                      x={AXIS_GUTTER + columnIndex * CELL_WIDTH}
                      y={rowIndex * CELL_HEIGHT}
                      width={CELL_WIDTH - 1}
                      height={CELL_HEIGHT - 1}
                    />
                  );
                })}
              </g>
            ))}
            {SIMILARITY_AXIS.map((similarity, columnIndex) => (
              <text
                key={similarity}
                className="rr-info-matrix-axis"
                x={AXIS_GUTTER + columnIndex * CELL_WIDTH + (CELL_WIDTH - 1) / 2}
                y={DELTA_AXIS.length * CELL_HEIGHT + 12}
                textAnchor="middle"
              >
                {formatAxisValue(similarity)}
              </text>
            ))}
            <text
              className="rr-info-matrix-axis-title"
              x={AXIS_GUTTER}
              y={MATRIX_HEIGHT - 4}
              textAnchor="start"
            >
              similarity (s1) →
            </text>
            <text className="rr-info-matrix-axis-title" x={0} y={10} textAnchor="start">
              ↑ delta
            </text>
          </svg>
          <div className="rr-info-legend">
            <span className="rr-info-legend-item">
              <span className="rr-info-legend-swatch is-confident" />
              Confident
            </span>
            <span className="rr-info-legend-item">
              <span className="rr-info-legend-swatch is-unconfident" />
              Unconfident
            </span>
          </div>
          <div className="rr-info-detail">
            <div className="rr-info-formula">
              C_{RECOGNITION_CONFIDENCE_EXPONENT} = (s1 − s2) / (1 − s2)^
              {RECOGNITION_CONFIDENCE_EXPONENT}
            </div>
            <ul className="rr-info-detail-list">
              <li>
                {/* The threshold is derived, not a round number, so it is printed to three
                    decimals rather than dumped at full float precision. */}
                Confident when C ≥ {RECOGNITION_CONFIDENCE_THRESHOLD.toFixed(3)} on the Atlas
                display scale (the equivalent raw cosine threshold is{' '}
                {RECOGNITION_CONFIDENCE_THRESHOLD_RAW_COSINE}; this is not a similarity conversion).
              </li>
              <li>
                s1 is the top candidate&rsquo;s similarity; s2 is the best candidate with a
                different item number, after applying captured design-link evidence.
              </li>
              <li>
                When no different-item candidate was returned, s2 falls back to the runner-up floor{' '}
                {RECOGNITION_RUNNER_UP_FLOOR} rather than leaving confidence unavailable.
              </li>
              <li>
                Thresholds are provisional and versioned; they are recalibrated from verified
                representative samples and never rewrite historical outcomes.
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfidenceMatrixInfo;
